import React from 'react'
import styled from 'styled-components'

// Components
import Activity from 'modules/activity/components/Activity'
import { Tabs, Tab, TabList, TabPanel } from 'components/Tabs'
import Breakpoint from 'components/Breakpoint'
import Heading from 'components/Heading'
import Inspector from 'modules/shares/Inspector/Sidebar/components/Inspector'

// Types
import { VersionFragment } from 'gqlTypes'
import { InspectorState } from 'modules/shares/Inspector/types'
import { Layer, ArtboardInSidebar, ShareInSidebar } from 'modules/shares/types'

// Hooks
import { useThreadRouteParams } from 'utils/hooks/useThreadRouteParams'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  border-radius: 0;
  -webkit-overflow-scrolling: touch;
  background-color: ${({ theme }) => theme.colors.background.light.base};
`

const Header = styled(Heading.H3)`
  padding: 22px 27px 21px; /* stylelint-disable-line scales/space */
  margin: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.dark.base};
  position: relative;
  line-height: 1.5;
`

interface DetailSidebarProps {
  artboard: ArtboardInSidebar
  inspector: InspectorState
  inspectorError?: string
  inspectorErrorReason?: string
  userCanInspect: boolean
  inspectorReady: boolean
  share: ShareInSidebar
  currentVersion: VersionFragment
  selectedLayer?: Layer
  selectedTab?: TabOptions
  onTabSelected: (tabOption: TabOptions) => void
}

export enum TabOptions {
  ACTIVITY = 'Activity',
  INSPECTOR = 'Inspector',
}

/**
 * Returns the corresponding TabOption from a string
 * It returns the default arg when the TabOption is not valid
 */
export function getTabOptionFromString(
  defaultTabOption: TabOptions,
  hash: string
) {
  switch (hash.toLowerCase()) {
    case 'activity':
      return TabOptions.ACTIVITY
    case 'inspector':
      return TabOptions.INSPECTOR
    default:
      return defaultTabOption
  }
}

const DetailSidebar = ({
  artboard,
  inspector,
  share,
  currentVersion,
  userCanInspect = false,
  inspectorReady,
  inspectorError,
  inspectorErrorReason,
  selectedTab,
  onTabSelected,
  selectedLayer,
}: DetailSidebarProps) => {
  const { isThreadRoute } = useThreadRouteParams()

  const ActivityComponent = <Activity share={share} artboard={artboard} />
  const InspectorComponent = (
    <Inspector
      isPage={artboard.isPage}
      ready={inspectorReady}
      error={inspectorError}
      errorReason={inspectorErrorReason}
      inspector={inspector}
      layer={selectedLayer}
      shareIdentifier={share.identifier}
      currentVersion={currentVersion}
      permanentArtboardShortId={artboard.permanentArtboardShortId}
    />
  )

  return (
    <Container>
      <Breakpoint on="sm">
        {matches => {
          const isMobile = !matches

          if (isMobile || !userCanInspect) {
            return (
              <>
                <Header>Activity</Header>
                {ActivityComponent}
              </>
            )
          } else {
            const tabs = [TabOptions.ACTIVITY, TabOptions.INSPECTOR]
            const selectedTabIndex = tabs.indexOf(selectedTab || tabs[0])

            return (
              <Tabs
                onTabSelected={index => onTabSelected(tabs[index])}
                selectedTabIndex={selectedTabIndex}>
                {isThreadRoute ? null : (
                  <TabList>
                    <Tab urlFragment={TabOptions.ACTIVITY}>
                      {TabOptions.ACTIVITY}
                    </Tab>
                    <Tab urlFragment={TabOptions.INSPECTOR}>
                      {TabOptions.INSPECTOR}
                    </Tab>
                  </TabList>
                )}

                <TabPanel>{ActivityComponent}</TabPanel>
                <TabPanel>{InspectorComponent}</TabPanel>
              </Tabs>
            )
          }
        }}
      </Breakpoint>
    </Container>
  )
}

export default DetailSidebar
